import type {
  BalanceDue,
  ExpectedPickup,
  LineFulfillment,
  LineMerchandise,
  OrderChangeAction,
  OrderLineInput,
  OrderLineKind,
  OrderMeasurements,
  OrderPickup,
  OrderStatus,
} from '@fitting-room/contracts'
import {
  EXPECTED_PICKUP_WEEKS,
  MERCHANDISE_KINDS,
  ORDER_MEASUREMENT_KEYS,
  discountCapMinor,
  discountOverCap,
  lineDiscountMinor,
  lineNetMinor,
  expectedPickup,
  orderTotals,
  type LineDiscount,
  type OrderTotalsValue,
} from '@fitting-room/contracts/order-math'
import { bpsFromPercent } from './backOffice'
import { formatUsd, minorFromDollars } from './appointments'

/**
 * The words and the arithmetic the order screens share, kept out of the
 * components so they can be tested in `node` like the rest of `lib/`.
 *
 * The money itself — a line's discount, the order's tax — is
 * `@fitting-room/contracts/order-math`, the same module the API works it out
 * with, so the preview in the line form is the number the order will hold.
 */

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  open: 'Open',
  completed: 'Completed',
  canceled: 'Canceled',
}

/** The order the status chips are drawn in. */
export const ORDER_STATUS_ORDER: readonly OrderStatus[] = ['open', 'completed', 'canceled']

/**
 * How a line is bought, in the console's words. `made_to_order` reads
 * "Special order" — the owner's word at the counter for a gown ordered in
 * for this customer; the value keeps its name (`CLAUDE.md`, identifiers).
 */
export const LINE_KIND_LABEL: Record<OrderLineKind, string> = {
  made_to_order: 'Special order',
  off_the_rack: 'Off the rack',
  accessory: 'Accessory',
  alteration: 'Alteration',
  other: 'Other',
}

export const LINE_KIND_ORDER: readonly OrderLineKind[] = ['made_to_order', 'off_the_rack', 'accessory', 'alteration', 'other']

/** Where a line is on its way to the customer. `lib/fulfillment.ts` has the rest of the words for it. */
export const FULFILLMENT_LABEL: Record<LineFulfillment, string> = {
  none: '—',
  to_order: 'To order',
  ordered: 'Ordered',
  shipped: 'Shipped',
  arrived: 'Arrived',
  on_hold: 'On hold',
  picked_up: 'Picked up',
}

export const CHANGE_ACTION_LABEL: Record<OrderChangeAction, string> = {
  created: 'Written',
  updated: 'Edited',
  sales_staff_set: 'Bridal stylist',
  line_added: 'Line added',
  line_updated: 'Line edited',
  line_removed: 'Line removed',
  canceled: 'Canceled',
  restored: 'Restored',
  appointment_linked: 'Appointment linked',
  appointment_unlinked: 'Appointment unlinked',
  accounting_checked: 'Accounting checked',
  accounting_unchecked: 'Accounting unchecked',
  fulfillment_advanced: 'Moved on',
  fulfillment_undone: 'Step undone',
  tracking_updated: 'Tracking',
  picked_up_with_balance: 'Picked up with a balance',
  completed: 'Completed',
  reopened: 'Reopened',
  shopify_order_no_set: 'Shopify order #',
  invoice_emailed: 'Invoice emailed',
  arrival_notified: 'Customer told it arrived',
  arrival_notified_undone: 'Arrival notice undone',
  supplier_linked: 'Linked to Jovita',
  supplier_unlinked: 'Unlinked from Jovita',
  supplier_updated: 'Jovita update',
}

/** 975 → "9.75%". */
export function formatRate(bps: number): string {
  return `${Number((bps / 100).toFixed(2))}%`
}

/** Cents, always with them: an order is a receipt, and "$2,400" beside "$229.12" reads as a typo. */
export function formatCents(minor: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(minor / 100)
}

/** A line's discount, the way the line reads it: "10%" or "$300". */
export function discountLabel(kind: LineDiscount['kind'] | undefined, value: number | undefined): string | null {
  if (!kind || !value) return null
  return kind === 'percent' ? formatRate(value) : formatUsd(value)
}

/**
 * The discount as the stylist typed it — a percentage or a dollar amount —
 * in the API's terms. `null` for none, `undefined` for something that is
 * not a number the form can send.
 */
export function discountFromInput(kind: LineDiscount['kind'], raw: string): LineDiscount | null | undefined {
  if (raw.trim() === '') return null
  if (kind === 'percent') {
    const bps = bpsFromPercent(raw)
    if (bps === undefined) return undefined
    return bps === null || bps === 0 ? null : { kind, value: bps }
  }
  const minor = minorFromDollars(raw)
  if (minor === undefined) return undefined
  return minor === null || minor === 0 ? null : { kind, value: minor }
}

export interface LinePreview {
  grossMinor: number
  discountMinor: number
  /** Price × quantity + custom cost − discount: what the order will store as the line's total. */
  netMinor: number
  /** The cap's message when the discount is over it — the API's words, ahead of the API. */
  overCap: string | null
}

/**
 * What the line in the form will come to, worked out the way the API will:
 * on `lineNetMinor`, the same function, so a custom cost lands in the
 * preview exactly as it lands on the order — after the discount, and never
 * part of what the discount or its cap is a share of.
 */
export function previewLine(
  quantity: number,
  unitPriceMinor: number,
  discount: LineDiscount | null,
  maxDiscountPct: number,
  customCostMinor = 0,
): LinePreview {
  const grossMinor = quantity * unitPriceMinor
  const discountMinor = lineDiscountMinor(grossMinor, discount)
  const overCap = discountOverCap(discount, grossMinor, maxDiscountPct)
    ? `Over the ${maxDiscountPct}% cap — the most this line can come off is ${formatCents(discountCapMinor(grossMinor, maxDiscountPct))}.`
    : null
  return { grossMinor, discountMinor, netMinor: lineNetMinor({ quantity, unitPriceMinor, customCostMinor, discountMinor }), overCap }
}

/* ------------------------------ the order page ----------------------------- */

/** The order page's groups, as the invoice draws them. `other` holds the older alteration and other lines. */
export const GROUP_LABEL: Record<LineMerchandise, string> = {
  dress: 'Dresses',
  accessory: 'Accessories',
  shoes: 'Other accessories',
  other: 'Other',
}

/** What one thing in a group is called, for a line written without a style number. */
export const GROUP_ITEM_WORD: Record<LineMerchandise, string> = {
  dress: 'Dress',
  accessory: 'Accessory',
  shoes: 'Bridal heels',
  other: 'Other',
}

export const GROUP_ORDER: readonly LineMerchandise[] = ['dress', 'accessory', 'shoes', 'other']

/** The kinds a line in this group may be written as now — the older `accessory` kind is read, never offered. */
export function kindsForGroup(group: LineMerchandise): OrderLineKind[] {
  return MERCHANDISE_KINDS[group].filter((kind) => kind !== 'accessory')
}

/**
 * A line's Type on the order page. The older `accessory` kind — and every
 * accessory the ClickUp import writes, since ClickUp never said — is neither
 * a special order nor off the rack as far as anyone recorded, so it reads
 * "—" rather than as a third way to buy.
 */
export function lineTypeLabel(kind: OrderLineKind): string {
  return kind === 'accessory' ? '—' : LINE_KIND_LABEL[kind]
}

/** A line's title, the way the shop names a gown: its style number; the description when it has none. */
export function lineTitle(line: { readonly styleCode?: string | undefined; readonly description: string }): string {
  return line.styleCode?.trim() || line.description
}

export const MEASUREMENT_LABEL: Record<keyof OrderMeasurements, string> = {
  bust: 'Bust',
  waist: 'Waist',
  hip: 'Hip',
  wh: 'WH',
  hh: 'HH',
}

/** Where the expected pickup came from, said under it so a stylist knows how far to trust it. */
export function expectedPickupSource(source: ExpectedPickup['source']): string {
  switch (source) {
    case 'set':
      // Typed in the console, or read off ClickUp by the import: set on the
      // order, either way, not worked out.
      return 'Set on the order'
    case 'eta':
      return 'From the special order’s expected arrival'
    case 'held':
      return 'The day the piece is held until'
    case 'default':
      return `Order date + ${EXPECTED_PICKUP_WEEKS} weeks, until there’s an arrival date`
  }
}

/** Where the pickup date came from, said under it: the customer's own booking, or a day typed here. */
export function pickupDateSource(pickup: OrderPickup, time?: string): string {
  return pickup.source === 'appointment' ? `Their pickup appointment${time ? `, ${time}` : ''}` : 'Set on the order'
}

/**
 * Whether the pickup day comes before the gown is expected in. Only against
 * an expected pickup somebody knows — a supplier's ETA, or a day set on the
 * order; the worked-out default and a hold's last day are no reason to
 * doubt a day the customer booked.
 */
export function pickupComesEarly(pickupDay: string | undefined, expected: Pick<ExpectedPickup, 'source' | 'date'>): boolean {
  return Boolean(pickupDay) && (expected.source === 'eta' || expected.source === 'set') && pickupDay! < expected.date
}

/**
 * Is a pickup still ahead for this order — the list row's "Pickup <date>" and
 * the order page's Expected pickup, so the two never disagree, and the
 * "Pickup within 14 days" chip reads the same three facts. Not canceled, with
 * something on it, and not everything gone home yet.
 */
export function pickupAhead(order: { readonly status: OrderStatus; readonly lineCount: number; readonly allPickedUp: boolean }): boolean {
  return order.status !== 'canceled' && order.lineCount > 0 && !order.allPickedUp
}

/** Where the balance due date came from: by hand, or the expected pickup it follows. */
export function balanceDueSource(source: BalanceDue['source']): string {
  switch (source) {
    case 'set':
      return 'Set by hand'
    case 'pickup':
      return 'On the expected pickup day'
  }
}

/**
 * The history as the screen reads it. Entries are worded when they are
 * written, and the ones written before the console's words changed say
 * "Made to order" and "Sales staff"; they read the way the page around them
 * does now.
 */
export function changeLogText(text: string): string {
  // Only the word an entry starts with — the line's kind, or the field's
  // name — which the API wrote. Anything after it may be what somebody
  // typed, a description or a reason, and is theirs as written.
  return text.replace(/^Made to order\b/, 'Special order').replace(/^Sales staff:/, 'Bridal stylist:')
}

/* ------------------------------- a new order ------------------------------- */

/**
 * One line as the new-order page holds it while it is typed: text, the way
 * the fields hold it, turned into an `OrderLineInput` only on submit.
 */
export interface LineDraft {
  /** Stable across renders, for React's key. */
  key: string
  merchandise: LineMerchandise
  kind: OrderLineKind
  styleCode: string
  color: string
  size: string
  price: string
  customCost: string
  discountMode: 'none' | 'percent' | 'amount'
  discountText: string
  /** A catalog style, when one was picked. A free style number needs none. */
  garmentId?: string | undefined
  /** A rack piece, when one was picked. Off the rack is written by style number alone just as well. */
  rackItemId?: string | undefined
  /** Off the rack: the customer takes it home today. */
  takenToday: boolean
}

let draftSeq = 0

export function newDraft(merchandise: LineMerchandise): LineDraft {
  draftSeq += 1
  return {
    key: `draft-${draftSeq}`,
    merchandise,
    // Heels come off the shelf; a dress or an accessory starts as the usual special order.
    kind: merchandise === 'shoes' ? 'off_the_rack' : 'made_to_order',
    styleCode: '',
    color: '',
    size: '',
    price: '',
    customCost: '',
    discountMode: 'none',
    discountText: '',
    takenToday: false,
  }
}

/** Nothing typed into it: an "Add more" row left empty is skipped on submit, not refused. */
export function draftIsBlank(d: LineDraft): boolean {
  return (
    !d.styleCode.trim() &&
    !d.color.trim() &&
    !d.size.trim() &&
    !d.price.trim() &&
    !d.customCost.trim() &&
    !d.discountText.trim() &&
    !d.rackItemId &&
    !d.garmentId
  )
}

export interface DraftCheck {
  /** The line as the API takes it, when it can be sent. */
  input?: OrderLineInput
  /** Field → what is wrong with it. */
  problems: Partial<Record<'price' | 'customCost' | 'discount', string>>
  /** What it comes to, when the numbers in it are numbers. */
  preview: LinePreview | null
  customCostMinor: number
}

/** A draft checked and priced, the way the API will check and price it. */
export function checkDraft(d: LineDraft, maxDiscountPct: number): DraftCheck {
  const problems: DraftCheck['problems'] = {}
  const price = minorFromDollars(d.price)
  const custom = minorFromDollars(d.customCost)
  const discount = d.discountMode === 'none' ? null : discountFromInput(d.discountMode, d.discountText)
  if (price === undefined || price === null) problems.price = 'A price, like 2,400.'
  if (custom === undefined) problems.customCost = 'An amount, like 215.'
  if (discount === undefined) problems.discount = d.discountMode === 'percent' ? 'A percentage, like 12.5.' : 'An amount, like 150.'
  const customCostMinor = custom ?? 0
  const preview =
    typeof price === 'number' && discount !== undefined && custom !== undefined
      ? previewLine(1, price, discount, maxDiscountPct, customCostMinor)
      : null
  if (preview?.overCap) problems.discount = preview.overCap
  if (Object.keys(problems).length > 0 || typeof price !== 'number') return { problems, preview, customCostMinor }
  const style = d.styleCode.trim()
  const input: OrderLineInput = {
    kind: d.kind,
    merchandise: d.merchandise,
    description: style || GROUP_ITEM_WORD[d.merchandise],
    ...(style ? { styleCode: style } : {}),
    ...(d.garmentId && d.kind === 'made_to_order' ? { garmentId: d.garmentId } : {}),
    ...(d.rackItemId && d.kind === 'off_the_rack' ? { rackItemId: d.rackItemId } : {}),
    ...(d.size.trim() ? { size: d.size.trim() } : {}),
    ...(d.color.trim() ? { color: d.color.trim() } : {}),
    quantity: 1,
    unitPriceMinor: price,
    ...(customCostMinor ? { customCostMinor } : {}),
    discount: discount ?? null,
    ...(d.kind === 'off_the_rack' && d.takenToday ? { takenToday: true } : {}),
  }
  return { problems, preview, input, customCostMinor }
}

/**
 * The new order's money, from the lines that price, at the rate it will be
 * written at — `orderTotals`, what the API will store.
 */
export function draftTotals(drafts: readonly LineDraft[], taxRateBps: number, maxDiscountPct: number): OrderTotalsValue {
  const lines = drafts.flatMap((d) => {
    const { preview, customCostMinor } = checkDraft(d, maxDiscountPct)
    return preview
      ? [{ quantity: 1, unitPriceMinor: preview.grossMinor, customCostMinor, discountMinor: preview.discountMinor, taxable: true }]
      : []
  })
  return orderTotals(lines, taxRateBps)
}

/** What a percentage of the total comes to, to the cent. */
export function depositFromPercent(totalMinor: number, percent: number): number {
  return Math.round((totalMinor * percent) / 100)
}

/** What share of the total a deposit is, to two places at most: "50", "33.33". */
export function percentOfTotal(totalMinor: number, depositMinor: number): string {
  if (totalMinor <= 0) return ''
  return String(Number(((depositMinor / totalMinor) * 100).toFixed(2)))
}

/**
 * When a new order's customer is expected before any arrival or held-until
 * date is known — `expectedPickup` with nothing to go on, the order date + 20
 * weeks — and so when its balance falls due.
 */
export function newOrderPickupDay(orderDay: string): string {
  return expectedPickup({ orderDay, lines: [] }).date
}

/** The measurements panel as typed: every field a string, blank for none. */
export interface OrderNotesDraft {
  readonly measurements: Readonly<Record<keyof OrderMeasurements, string>>
  readonly otherRequest: string
  readonly note: string
}

/** The panel's fields filled from the order. */
export function orderNotesDraft(order: {
  readonly measurements?: OrderMeasurements | undefined
  readonly otherRequest?: string | undefined
  readonly note?: string | undefined
}): OrderNotesDraft {
  const m = order.measurements ?? {}
  return {
    measurements: { bust: m.bust ?? '', waist: m.waist ?? '', hip: m.hip ?? '', wh: m.wh ?? '', hh: m.hh ?? '' },
    otherRequest: order.otherRequest ?? '',
    note: order.note ?? '',
  }
}

/**
 * What the panel sends to `PUT /api/admin/orders/:id`: only what changed, and
 * no reason — these three need none (`REASONLESS_ORDER_FIELDS`). A blank that
 * was something is `null`, cleared; the measurements go as all five or not at
 * all, because the API replaces them whole. `null` when nothing changed, so
 * Save writes nothing to the history.
 */
export function orderNotesChanges(
  order: Parameters<typeof orderNotesDraft>[0],
  draft: OrderNotesDraft,
): { measurements?: OrderMeasurements | null; otherRequest?: string | null; note?: string | null } | null {
  const body: { measurements?: OrderMeasurements | null; otherRequest?: string | null; note?: string | null } = {}
  const text = (now: string, was: string | undefined) => (now.trim() === (was ?? '') ? undefined : now.trim() || null)
  const otherRequest = text(draft.otherRequest, order.otherRequest)
  if (otherRequest !== undefined) body.otherRequest = otherRequest
  const note = text(draft.note, order.note)
  if (note !== undefined) body.note = note
  const before = order.measurements ?? {}
  const keys = ORDER_MEASUREMENT_KEYS
  const cleaned: OrderMeasurements = Object.fromEntries(keys.flatMap((key) => (draft.measurements[key].trim() ? [[key, draft.measurements[key].trim()]] : [])))
  if (keys.some((key) => (cleaned[key] ?? '') !== (before[key] ?? ''))) body.measurements = Object.keys(cleaned).length > 0 ? cleaned : null
  return Object.keys(body).length > 0 ? body : null
}
